/*
 * (c) Meta Platforms, Inc. and its affiliates.
 */

import React from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { Map as MapFB } from "react-map-gl";
import {
	Source,
	Layer,
	Marker,
	LayerProps,
	MapRef,
	GeoJSONSource,
	ViewStateChangeEvent,
} from "react-map-gl";
import MetaDataCard from "../cards/MetaDataCard";
import maplibregl from "maplibre-gl";
import { calcImgScale, IMG_ZOOM_START } from "../../utils/Helpers";
import {
	FeatureCollection,
	DataPoint,
	ViewStateMap,
} from "../../utils/DemoTypes";
import { useDebounce } from "react-use";

interface MapVizClustersProps {
	data: FeatureCollection;
	setShowImgGrid: (show: boolean) => void;
	setGridData: (features: GeoJSON.Feature<GeoJSON.Geometry>[]) => void;
}

export interface MetadaDataCardData {
	[key: string]: string | number;
}

const clusterLayer: LayerProps = {
	id: "clusters",
	type: "circle",
	source: "my-data",
	filter: ["has", "point_count"],
	paint: {
		"circle-color": [
			"step",
			["get", "point_count"],
			"#FBBD23",
			100,
			"#36D399",
			750,
			"#515E91",
		],
		"circle-radius": ["step", ["get", "point_count"], 20, 100, 30, 750, 40],
	},
};

const clusterCountLayer: LayerProps = {
	id: "cluster-count",
	type: "symbol",
	source: "my-data",
	filter: ["has", "point_count"],
	//filter: ['!=', 'cluster', false],
	layout: {
		"text-field": "{point_count_abbreviated}",
		//"text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
		"text-size": 12,
	},
};

const unclusteredPointLayer: LayerProps = {
	id: "unclustered-point",
	type: "circle",
	source: "my-data",
	filter: ["!", ["has", "point_count"]],
	paint: {
		"circle-color": "#11b4da",
		"circle-radius": 5,
		"circle-stroke-color": "#fff",
		"circle-stroke-width": 1,
		"circle-opacity": [
			"interpolate",
			["linear"],
			["zoom"],
			// zoom is 12 (or less)
			12,
			1,
			// zoom is 13 (or greater)
			13,
			0.25,
		],
	},
};

const MapViz = ({ data, setShowImgGrid, setGridData }: MapVizClustersProps) => {
	const mapRef = React.useRef<MapRef>(null!);
	const [popupInfo, setPopupInfo] = React.useState<MetadaDataCardData | null>(
		null
	);
	const [imgs, setImgs] = React.useState<any>();
	const [currentQueryParameters, setSearchParams] = useSearchParams();
	const view_state = currentQueryParameters.get("at") as string;
	const location = useLocation();
	const newQueryParameters: URLSearchParams = new URLSearchParams(
		location.search
	);

	const [viewState, setViewState] = React.useState<ViewStateMap>({
		longitude: parseFloat(view_state?.split(",")[0] ?? "-24"),
		latitude: parseFloat(view_state?.split(",")[1] ?? "40.67"),
		zoom: parseFloat(view_state?.split(",")[2] ?? "2.5"),
	});

	const InitViewState = {
		longitude: parseFloat(view_state?.split(",")[0] ?? "-24"),
		latitude: parseFloat(view_state?.split(",")[1] ?? "40.67"),
		zoom: parseFloat(view_state?.split(",")[2] ?? "2.5"),
	};

	useDebounce(
		() => {
			newQueryParameters.set(
				"at",
				`${viewState?.longitude},${viewState?.latitude},${viewState?.zoom}`
			);
			setSearchParams(newQueryParameters, { replace: true });
		},
		250,
		[viewState]
	);

	/**
	 * Handles logic when click event on cluster and markers
	 */
	const onClick = React.useCallback(
		(event: mapboxgl.MapLayerMouseEvent) => {
			const feature = event.features && event.features[0];
			if (!feature) return;
			const layer = feature.layer;

			if (layer.id === "clusters") {
				const clusterId = feature.properties?.cluster_id;
				const mapboxSource = mapRef.current.getSource(
					"my-data"
				) as GeoJSONSource;

				// Get the leaves in cluster on click.
				mapboxSource.getClusterLeaves(
					clusterId,
					500,
					0,
					(err, features: GeoJSON.Feature<GeoJSON.Geometry>[]) => {
						if (err) return;
						setGridData(features);
						setShowImgGrid(true);
						setPopupInfo(null);
					}
				);

				// Expand cluster cluster on click
				//mapboxSource.getClusterExpansionZoom(clusterId, (err, zoom) => {
				//	if (err) return;
				//
				//	mapRef.current.easeTo({
				//		center: (feature.geometry as { coordinates: number[] })
				//			.coordinates as mapboxgl.LngLatLike,
				//		zoom,
				//		duration: 500,
				//	});
				//});
			} else {
				event.originalEvent.stopPropagation();
				setPopupInfo(feature.properties);
				setShowImgGrid(false);
			}
		},
		[setGridData, setShowImgGrid]
	);

	function drawImgs(newSet: any) {
		if (newSet.size <= 0) {
			setImgs(null);
		}
		setImgs(newSet);
	}

	/**
	 * Handles logic when moving on the map, triggers on
	 * zooming and panning.
	 */
	const onMove = React.useCallback(
		(event: ViewStateChangeEvent) => {
			const mapboxSource = mapRef.current;
			const currentZoomLevel = event.viewState.zoom;

			// Keep the view state as moving on the map.
			setViewState(event.viewState);

			// Calculate how to scale images based on zoom level
			const scale = calcImgScale(currentZoomLevel);

			if (currentZoomLevel > IMG_ZOOM_START) {
				const features = mapboxSource
					.querySourceFeatures("my-data")
					.filter(
						(i: mapboxgl.MapboxGeoJSONFeature) =>
							i.properties && (!i.properties.cluster as boolean)
					);

				let newSet = features.reduce((map, point: any, i) => {
					map.set(point.properties.image_id, {
						id: point.properties.image_id,
						scale: scale,
						...point.properties,
					});
					return map;
				}, new Map());

				drawImgs(new Map<string, DataPoint>(newSet));
				setShowImgGrid(false);
			} else {
				drawImgs(new Map<string, DataPoint>());
			}
		},
		[setShowImgGrid]
	);

	/**
	 * Markers as pins.
	 */
	const markers: any = React.useMemo(
		() => [], // eslint-disable-next-line
		[imgs]
	);

	imgs?.forEach((i: any, index: number) => {
		const img_width = 95 * i.scale; // 95px as the default size.
		markers.push(
			<Marker
				key={`marker-${index}`}
				longitude={i.lng}
				latitude={i.lat}
				anchor="center"
				onClick={(e) => {
					e.originalEvent.stopPropagation();
					setPopupInfo(i);
				}}
			>
				<div style={{ width: `${img_width}px` }}>
					<img className="rounded-md" width="100%" src={i.url} alt="" />
				</div>
			</Marker>
		);
	});

	return (
		<React.Fragment>
			<MapFB
				ref={mapRef}
				initialViewState={InitViewState}
				mapLib={maplibregl}
				mapStyle="https://external.xx.fbcdn.net/maps/vt/style/grayscale/"
				interactiveLayerIds={[
					clusterLayer.id as string,
					unclusteredPointLayer.id as string,
				]}
				minZoom={1.25}
				maxZoom={20}
				onClick={onClick}
				onMove={onMove}
			>
				{markers}
				<Source
					id="my-data"
					type="geojson"
					data={data}
					cluster={true}
					clusterMaxZoom={11}
					clusterRadius={100}
				>
					<Layer {...clusterLayer} />
					<Layer {...clusterCountLayer} />
					<Layer {...unclusteredPointLayer} />
				</Source>
			</MapFB>
			{popupInfo && (
				<MetaDataCard data={popupInfo} setPopupInfo={setPopupInfo} />
			)}
		</React.Fragment>
	);
};

export default MapViz;
